import React, { useState } from "react";
import {
    Button,
    Divider,
    FormHelperText,
    Grid,
    IconButton,
    InputAdornment,
    Link as ExternalLink,
    Stack,
    Tooltip,
} from "@mui/material";
import Icon from "src/components/icon";
import styled from "styled-components";
import github from "src/assets/icons/github";
import Link from "src/components/link";
import { Helmet } from "react-helmet";
import { LoadingButton, ToggleButton } from "@mui/lab";
import TextField from "@mui/material/TextField";
import { useNavigate } from "react-router-dom";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import request from "../../../utils/request";
import patPermissionsScreen from "../../../assets/images/patPermissionsScreen.png";

const H1 = styled.h1`
    font-size: 22px;
    margin: 0;
    font-weight: normal;
    color: ${({ theme }) => theme.palette.text.secondary};
`;

const H2 = styled.h2`
    font-size: 14px;
    font-weight: normal;
    margin: 0;
    color: ${({ theme }) => theme.palette.primary.main};
`;

const P = styled.h2`
    font-size: 12px;
    font-weight: normal;
    margin: 0;
    ${({ theme }) => theme.breakpoints.up("sm")} {
        width: 60%;
    }
    color: ${({ theme }) => theme.palette.secondary.main};
`;

const PermissionsIcon = styled(Icon)`
    background-color: #fff;
    border-radius: 16px;
    padding: 24px 12px 24px 12px;
    margin: 32px 0 32px 0;
`;

const StyledToggleButton = styled(ToggleButton)`
    font-size: 12px;
    text-transform: none;
    border-radius: 8px;
    margin: 6px;
    font-weight: 300;
    padding-left: 8px;
    width: 164px;
    height: 45px;
`;

const TextPlaceholder =
    "The permissions we request on Github help us provide you with detailed insights. Right now this means we have to ask for both write and read access because Github does not support read-only access for Oauth applications. While we would like to request read-only permissions, currently this is not possible. You can read about & request Github to add read-only permissions ";

const ContactPlaceholder =
    "In case of any inquiry regarding software permissions, please do not hesitate to ";

const PermissionsGithub = () => {
    const [error, setError] = useState();
    const [type, setType] = useState();
    const [token, setToken] = React.useState({ value: undefined });
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = React.useState(null);
    const navigate = useNavigate();

    const checkTokenValue = () => {
        let tokenError;
        if (!token.value || token.value.length < 5) {
            tokenError = "Please enter a valid personal access token";
        } else if (token.value.startsWith("github_pat_")) {
            tokenError = "Only github classic tokens are supported, not fine-grained ones";
        } else if (!token.value.startsWith("ghp_")) {
            tokenError = "Only github classic tokens are supported (they start with 'ghp_')";
        }
        if (tokenError) {
            setToken({
                ...token,
                error: tokenError,
            });
            return tokenError;
        }
        return undefined;
    };

    const changeTokenValue = (event) => {
        setToken({
            ...token,
            error: undefined,
            value: event.target.value,
        });
    };

    const handleTypeChange = (newType) => {
        if (newType !== type) {
            setError(undefined);
            setType(newType);
        }
    };

    const handleClick = async () => {
        if (!type) {
            setError("Select how you want to integrate");
        } else if (type === "token" && !token) {
            setError("Token is required");
        } else if (type === "token" && token) {
            const tokenError = checkTokenValue();
            if (!tokenError) {
                try {
                    setLoading(true);
                    await request(`/api/github/connect`, {
                        method: "POST",
                        body: JSON.stringify({
                            token: token.value,
                        }),
                    });
                    navigate("/integrations/success/github");
                } catch (e) {
                    setLoading(false);
                    if (e.status === 400) {
                        setError(e.message);
                    } else {
                        navigate("/integrations/failure/github");
                    }
                }
            }
        } else {
            window.location.replace(
                `https://github.com/login/oauth/authorize?scope=user:email%20repo&client_id=${process.env.GITHUB_CLIENT_ID}`
            );
        }
    };

    const getTooltipText = () => {
        if (!type) return "Select an integration type first";
        if (type === "token" && (!token.value || token.error))
            return "Valid token is required for this setup";
        return "";
    };

    const tooltipText = getTooltipText();

    return (
        <>
            <Helmet>
                <title>Adadot - GitHub Permissions</title>
            </Helmet>
            <Grid container justifyContent="center" mb={4}>
                <Stack
                    direction="column"
                    alignItems="flex-start"
                    style={{ height: "100%", width: "90%" }}
                >
                    <H1>Github Integration</H1>
                    <H2>Select a method of integration with GitHub data</H2>
                    <Stack direction="row" alignItems="center">
                        <PermissionsIcon svg={github} width="100px" height="127" />
                        <Stack direction="row" justifyContent="center" ml={2}>
                            <StyledToggleButton
                                value="oauth"
                                selected={type === "oauth"}
                                onClick={() => handleTypeChange("oauth")}
                            >
                                One click integration
                            </StyledToggleButton>
                            <Divider orientation="vertical" flexItem />
                            <StyledToggleButton
                                value="admin"
                                selected={type === "token"}
                                onClick={() => handleTypeChange("token")}
                            >
                                Full permission control
                            </StyledToggleButton>
                        </Stack>
                    </Stack>
                    {type === "token" && (
                        <>
                            <P>
                                For full control over permissions you need to{" "}
                                <ExternalLink
                                    href="https://github.com/settings/tokens/new"
                                    underline="hover"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    generate a GitHub personal access token
                                </ExternalLink>{" "}
                                and enter it on the field bellow.
                            </P>
                            <P style={{ marginTop: "24px" }}>
                                &#8226; Mandatory permissions are shown on the image.
                            </P>
                            <P>
                                &#8226; We recommend issuing a <strong>long lived token</strong> to
                                ensure uninterrupted data flow.
                            </P>
                            <img
                                src={patPermissionsScreen}
                                alt=""
                                style={{ maxWidth: "100vw", maxHeight: "44vh" }}
                            />
                            <Stack direction="row" mt={2} style={{ width: "100%" }}>
                                <TextField
                                    required
                                    fullWidth
                                    style={{ maxWidth: "50%" }}
                                    label="Github Personal Access Token"
                                    variant="outlined"
                                    autoComplete="off"
                                    helperText={token.error}
                                    value={token.value}
                                    type={showPassword ? "text" : "password"}
                                    onChange={(event) => changeTokenValue(event)}
                                    onBlur={() => checkTokenValue()}
                                    onFocus={() => setToken({ ...token, error: undefined })}
                                    error={!!token.error}
                                    id="access-token-input"
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    aria-label="toggle token visibility"
                                                    onClick={() => setShowPassword(!showPassword)}
                                                    edge="end"
                                                >
                                                    {showPassword ? (
                                                        <VisibilityOff />
                                                    ) : (
                                                        <Visibility />
                                                    )}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                                {!token.value && (
                                    <Button
                                        variant="contained"
                                        style={{ width: "192px", marginLeft: "16px" }}
                                        href="https://github.com/settings/tokens/new"
                                        target="_blank"
                                    >
                                        Generate token here
                                    </Button>
                                )}
                            </Stack>
                        </>
                    )}
                    <P style={{ marginTop: "24px", fontWeight: "200" }}>
                        {TextPlaceholder}
                        <ExternalLink
                            href="https://github.com/jollygoodcode/jollygoodcode.github.io/issues/6"
                            underline="hover"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            here
                        </ExternalLink>{" "}
                        and{" "}
                        <ExternalLink
                            href="https://github.com/orgs/github-community/discussions/7891"
                            underline="hover"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            here
                        </ExternalLink>
                    </P>

                    <P style={{ marginTop: "12px", fontWeight: "200" }}>
                        {ContactPlaceholder}
                        <Link to="/upgrade/inquiry">contact us</Link>
                    </P>
                    <Tooltip title={tooltipText}>
                        <span>
                            <LoadingButton
                                loading={loading}
                                variant="contained"
                                color="primary"
                                type="submit"
                                onClick={handleClick}
                                style={{ width: "102px", marginTop: "32px", marginBottom: "32px" }}
                                disabled={!!tooltipText}
                            >
                                Continue
                            </LoadingButton>
                        </span>
                    </Tooltip>
                    {error && <FormHelperText error>{error}</FormHelperText>}
                </Stack>
            </Grid>
        </>
    );
};

export default PermissionsGithub;
